import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { randomUUID } from "../../lib/uuid";
import { Wifi } from "lucide-react";
import { FiscalDocumentType, PaymentMethod, type FiscalDocumentDto, type ProductDto } from "@finora/types";
import { ApiError } from "@finora/api-client";
import { api } from "../../lib/api";
import { usePosStore } from "../../store/pos-store";
import { ProductGrid } from "./ProductGrid";
import { Cart } from "./Cart";
import { CheckoutModal } from "./CheckoutModal";
import { ReceiptView } from "./ReceiptView";
import type { CreateAdvanceSaleFormInput } from "./AdvanceInvoiceSection";

export function PosPage() {
  const queryClient = useQueryClient();
  const [receiptDocument, setReceiptDocument] = useState<FiscalDocumentDto | null>(null);
  const { activeDocumentId, setActiveDocumentId, isCheckoutOpen, openCheckout, closeCheckout } =
    usePosStore();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [advanceSaleError, setAdvanceSaleError] = useState<string | null>(null);

  const { data: dashboard } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.getDashboard(),
  });

  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });

  const { data: document } = useQuery({
    queryKey: ["fiscal-document", activeDocumentId],
    queryFn: () => api.getFiscalDocument(activeDocumentId!),
    enabled: !!activeDocumentId,
  });

  const ensureDocument = useMutation({
    mutationFn: () =>
      api.createFiscalDocument({
        businessLocationId: dashboard!.defaultBusinessLocationId,
        type: FiscalDocumentType.FISCAL_RECEIPT,
      }),
    onSuccess: (doc) => setActiveDocumentId(doc.id),
  });

  const addItem = useMutation({
    mutationFn: async (product: ProductDto) => {
      // WooCommerce konektor (dodatak): ako je uključen, naziv i cena se povlače sa sajta po šifri (SKU).
      // Svaka greška/nedostupan sajt se ignoriše - kasa nastavlja sa podacima iz Finore.
      let name = product.name;
      let unitPriceGross = product.unitPriceGross;
      if (product.sku) {
        const woo = await api.lookupWooProduct(product.sku).catch(() => null);
        if (woo?.enabled && woo.found && woo.name && woo.price) {
          name = woo.name;
          unitPriceGross = woo.price;
        }
      }
      return api.addFiscalDocumentItem(activeDocumentId!, {
        productId: product.id,
        name,
        quantity: "1",
        unitPriceGross,
        taxRateId: product.taxRateId,
      });
    },
    onSuccess: (doc) => queryClient.setQueryData(["fiscal-document", doc.id], doc),
  });

  const updateItemQuantity = useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: string; quantity: string }) =>
      api.updateFiscalDocumentItem(activeDocumentId!, itemId, { quantity }),
    onSuccess: (doc) => queryClient.setQueryData(["fiscal-document", doc.id], doc),
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.removeFiscalDocumentItem(activeDocumentId!, itemId),
    onSuccess: (doc) => queryClient.setQueryData(["fiscal-document", doc.id], doc),
  });

  // Popust po stavci i popust na ceo račun (24.09.2026, korisnički zahtev) -
  // backend polja (item.discountAmount, doc.discountAmount) postoje od
  // PDV/popust zakrpe, kasa ih do sada nije nudila za unos.
  const updateItemDiscount = useMutation({
    mutationFn: ({ itemId, discountAmount }: { itemId: string; discountAmount: string }) =>
      api.updateFiscalDocumentItem(activeDocumentId!, itemId, { discountAmount }),
    onSuccess: (doc) => queryClient.setQueryData(["fiscal-document", doc.id], doc),
  });

  const updateDocumentDiscount = useMutation({
    mutationFn: (discountAmount: string) =>
      api.updateFiscalDocument(activeDocumentId!, { discountAmount }),
    onSuccess: (doc) => queryClient.setQueryData(["fiscal-document", doc.id], doc),
  });

  const checkout = useMutation({
    mutationFn: async (payments: { method: PaymentMethod; amount: string; receivedAmount?: string }[]) => {
      // Ako je prethodni pokušaj već registrovao uplatu (status je već
      // READY_FOR_FISCALIZATION ili je fiskalizacija prošli put pukla), NE
      // registrujemo platbu ponovo - to bi duplo uvećalo paidAmount. Samo
      // ponovo pokušavamo samu fiskalizaciju.
      const alreadyPaid =
        document?.status === "READY_FOR_FISCALIZATION" || document?.status === "FISCALIZATION_FAILED";
      if (!alreadyPaid) {
        for (const payment of payments) {
          const paymentKey = randomUUID();
          await api.registerPayment(
            activeDocumentId!,
            { method: payment.method, amount: payment.amount, receivedAmount: payment.receivedAmount },
            paymentKey,
          );
        }
      }
      const fiscalizeKey = randomUUID();
      return api.fiscalizeDocument(activeDocumentId!, { idempotencyKey: fiscalizeKey });
    },
    onSuccess: (doc) => {
      queryClient.setQueryData(["fiscal-document", doc.id], doc);
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      if (doc.status === "FISCALIZED") {
        closeCheckout();
        setActiveDocumentId(null);
        setCheckoutError(null);
        setReceiptDocument(doc);
      } else {
        setCheckoutError(
          doc.fiscalizationAttempts.at(-1)?.providerMessage ??
            "Fiskalizacija nije uspela. Pokušajte ponovo.",
        );
      }
    },
    onError: (err) => {
      setCheckoutError(err instanceof ApiError ? err.response.message : "Došlo je do greške.");
    },
  });

  // Avans-Prodaja (razgovor 23.09.2026) - pravi POTPUNO NOV dokument (jedna
  // "Аванс" stavka + odmah registrovana uplata, vidi
  // FiscalDocumentsService.createAdvanceSale), pa ga u istom koraku
  // fiskalizuje - kasir ne prolazi kroz običan Checkout, avans se ne dira
  // od korpe koju je eventualno već počeo da puni.
  const createAdvanceSale = useMutation({
    mutationFn: async (input: CreateAdvanceSaleFormInput) => {
      const idempotencyKey = randomUUID();
      const created = await api.createAdvanceSale(
        {
          businessLocationId: dashboard!.defaultBusinessLocationId,
          taxRateId: input.taxRateId,
          amount: input.amount,
          paymentMethod: input.paymentMethod,
          payments: input.payments,
          note: input.note,
          customerId: input.customerId,
          buyerPib: input.buyerPib,
          buyerName: input.buyerName,
          buyerIdType: input.buyerIdType,
          buyerAddress: input.buyerAddress,
        },
        idempotencyKey,
      );
      const fiscalizeKey = randomUUID();
      return api.fiscalizeDocument(created.id, { idempotencyKey: fiscalizeKey });
    },
    onSuccess: (doc) => {
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      if (doc.status === "FISCALIZED") {
        setAdvanceSaleError(null);
        setReceiptDocument(doc);
      } else {
        setAdvanceSaleError(
          doc.fiscalizationAttempts.at(-1)?.providerMessage ??
            "Fiskalizacija avansa nije uspela. Pokušajte ponovo.",
        );
      }
    },
    onError: (err) => {
      setAdvanceSaleError(err instanceof ApiError ? err.response.message : "Došlo je do greške.");
    },
  });

  async function handleSelectProduct(product: ProductDto) {
    if (!activeDocumentId) {
      const doc = await ensureDocument.mutateAsync();
      await api.addFiscalDocumentItem(doc.id, {
        productId: product.id,
        name: product.name,
        quantity: "1",
        unitPriceGross: product.unitPriceGross,
        taxRateId: product.taxRateId,
      });
      queryClient.invalidateQueries({ queryKey: ["fiscal-document", doc.id] });
    } else {
      addItem.mutate(product);
    }
  }

  const { data: serviceStatus } = useQuery({
    queryKey: ["fiscal-service-status", dashboard?.defaultBusinessLocationId],
    queryFn: () => api.getFiscalServiceStatus(dashboard?.defaultBusinessLocationId),
    enabled: !!dashboard,
    refetchInterval: 30_000,
  });

  function handleClearCart() {
    if (!document) return;
    if (!window.confirm("Da li sigurno želite da ispraznite račun?")) return;
    Promise.all(document.items.map((item) => api.removeFiscalDocumentItem(document.id, item.id))).then(
      () => queryClient.invalidateQueries({ queryKey: ["fiscal-document", document.id] }),
    );
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "F9") return;
      e.preventDefault();
      if (isCheckoutOpen || receiptDocument) return;
      if (!document || document.items.length === 0) return;
      // Ako je kasir jos u polju za popust po stavci (Cart.tsx), F9 do sada
      // NIJE pomerao fokus (za razliku od klika misem na dugme "Naplati
      // racun", koji prirodno blurira polje PRE otvaranja Checkout-a) - polje
      // je ostajalo fokusirano ISPOD modala, pa se popust commit-ovao tek
      // kasnije, kad se klikne na dugme UNUTAR Checkout-a - u međuvremenu je
      // Checkout vec prikazao/racunao STARI iznos (pre popusta), pa je
      // ispadalo kao da se popust duplira/ne slaze (razgovor 26.09.2026,
      // "da ne bude 2x popust"). Sad F9 PRVO blurira aktivno polje (isti
      // efekat kao tab/klik napolje - postojeci onBlur commit-uje popust),
      // pa TEK ONDA otvara Checkout.
      if (window.document.activeElement instanceof HTMLElement) {
        window.document.activeElement.blur();
      }
      setCheckoutError(null);
      openCheckout();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCheckoutOpen, receiptDocument, document, openCheckout]);

  return (
    // h-full (24.09.2026) - zamenjuje ranije "peglanje" magičnim brojevima
    // (h-[calc(100vh-6.5rem)], pa -10px) koje NIKAD nije do kraja uklonilo
    // skrol cele stranice. Pravi uzrok je bio nedostatak min-h-0 na <main>
    // u AppShell.tsx (vidi komentar tamo) - sa tim ispravljenim, h-full sad
    // ispravno nasleđuje tačnu preostalu visinu ekrana bez nagađanja broja.
    <div className="grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
      <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div className="mb-3 flex items-start justify-between">
          <h1 className="text-base font-semibold text-slate-900">Fiskalna kasa</h1>
          <div className="flex flex-col items-end gap-0.5">
            <span
              className={`flex items-center gap-1.5 text-xs font-medium ${
                serviceStatus?.status === "ONLINE"
                  ? "text-emerald-600"
                  : serviceStatus?.status === "DEGRADED"
                    ? "text-amber-600"
                    : "text-red-500"
              }`}
              title={
                serviceStatus?.status === "DEGRADED"
                  ? "PFX sertifikat i/ili PAK kod nisu uneti (Podešavanja -> Fiskalizacija)."
                  : serviceStatus?.status === "OFFLINE"
                    ? "Sertifikat i PAK su uneti, ali fiskalna služba ne odgovara. Detalji u pm2 logs finora-api."
                    : undefined
              }
            >
              <Wifi className="h-3.5 w-3.5" aria-hidden />
              {serviceStatus?.status === "ONLINE"
                ? "Servis povezan"
                : serviceStatus?.status === undefined
                  ? "Provera veze..."
                  : serviceStatus.status === "DEGRADED"
                    ? "Sertifikat nije podešen"
                    : "Servis nije dostupan"}
            </span>
            {/* JID/Lokacija (24.09.2026, korisnički zahtev; 25.09.2026 - sada PO
                LOKACIJI ako postoji registrovan FiscalDevice za nju, inace
                company-level - vidi getFiscalServiceStatus()), ne unose se ručno ovde. */}
            {(serviceStatus?.jid || serviceStatus?.location) && (
              <span className="text-[11px] leading-tight text-slate-400">
                {serviceStatus?.jid && <>JID: {serviceStatus.jid}</>}
                {serviceStatus?.jid && serviceStatus?.location && " · "}
                {serviceStatus?.location && <>Lokacija: {serviceStatus.location}</>}
              </span>
            )}
          </div>
        </div>
        <ProductGrid onSelectProduct={handleSelectProduct} />
      </div>

      <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <Cart
          document={document ?? null}
          onIncrement={(itemId, qty) =>
            updateItemQuantity.mutate({ itemId, quantity: (Number(qty) + 1).toString() })
          }
          onDecrement={(itemId, qty) => {
            const next = Number(qty) - 1;
            if (next <= 0) {
              removeItem.mutate(itemId);
            } else {
              updateItemQuantity.mutate({ itemId, quantity: next.toString() });
            }
          }}
          onRemove={(itemId) => removeItem.mutate(itemId)}
          onUpdateItemDiscount={(itemId, discountAmount) =>
            updateItemDiscount.mutate({ itemId, discountAmount })
          }
          onSetQuantity={(itemId, quantity) => updateItemQuantity.mutate({ itemId, quantity })}
          onClear={handleClearCart}
          onCheckout={() => {
            setCheckoutError(null);
            openCheckout();
          }}
          onCreateAdvanceSale={(input) => {
            setAdvanceSaleError(null);
            createAdvanceSale.mutate(input);
          }}
          isCreatingAdvanceSale={createAdvanceSale.isPending}
          createAdvanceSaleError={advanceSaleError}
        />
      </div>

      {isCheckoutOpen && document && (
        <CheckoutModal
          document={document}
          isSubmitting={checkout.isPending}
          errorMessage={checkoutError}
          defaultCombinedMethod={companySettings?.defaultCombinedPaymentMethod}
          onClose={closeCheckout}
          onConfirm={(payments) => checkout.mutate(payments)}
          onUpdateDiscount={(discountAmount) => updateDocumentDiscount.mutate(discountAmount)}
        />
      )}

      {receiptDocument && (
        <ReceiptView document={receiptDocument} onClose={() => setReceiptDocument(null)} />
      )}
    </div>
  );
}
